import { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Avatar,
  IconButton,
  Divider,
  Alert,
} from '@mui/material';
import { Button, GridLayout, Text } from '@qwickapps/react-framework';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import SaveIcon from '@mui/icons-material/Save';
import { useMiddlewareClient } from '@qwickforge/react-client';
import { LogoutButton } from '@qwickapps/auth-client';
import { useNavigate } from 'react-router-dom';

export default function ProfileSettings() {
  const client = useMiddlewareClient();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    displayName: '',
    email: '',
    bio: '',
    company: '',
    location: '',
    website: '',
    avatarUrl: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Load profile data
  useEffect(() => {
    const loadProfile = async () => {
      if (!client) return;

      try {
        const data = await client.profile.get();
        setFormData({
          displayName: data.displayName || '',
          email: data.email || '',
          bio: data.bio || '',
          company: data.company || '',
          location: data.location || '',
          website: data.website || '',
          avatarUrl: data.avatarUrl || '',
        });
      } catch (err) {
        console.error('Failed to load profile:', err);
        setError('Failed to load profile data');
      }
    };

    loadProfile();
  }, [client]);

  const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, [field]: event.target.value }));
  };

  const handleSave = async () => {
    if (!client) return;

    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      await client.profile.update({
        displayName: formData.displayName,
        avatarUrl: formData.avatarUrl || null,
        bio: formData.bio || null,
        company: formData.company || null,
        location: formData.location || null,
        website: formData.website || null,
      });

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error('Failed to save profile:', err);
      setError('Failed to save profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Text variant="h6" gutterBottom customColor="var(--theme-accent-dark)">
        Profile Information
      </Text>
      <Text variant="body2" style={{ marginBottom: '24px' }} customColor="var(--theme-text)">
        Update your personal information and profile picture
      </Text>

      <Divider sx={{ mb: 3 }} />

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Profile updated successfully!
        </Alert>
      )}

      {/* Profile Picture */}
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 3 }}>
        <Avatar
          sx={{ width: 100, height: 100 }}
          src={formData.avatarUrl}
        >
          {formData.displayName?.[0]?.toUpperCase() || formData.email?.[0]?.toUpperCase()}
        </Avatar>
        <Box>
          <Text variant="body2" gutterBottom customColor="var(--theme-text)">
            Profile Picture
          </Text>
          <IconButton
            aria-label="upload picture"
            component="label"
            size="small"
          >
            <input hidden accept="image/*" type="file" />
            <PhotoCamera sx={{ color: 'var(--theme-primary)' }} />
          </IconButton>
        </Box>
      </Box>

      {/* Form Fields */}
      <GridLayout columns={2} spacing="medium">
        <TextField
          fullWidth
          label="Display Name"
          value={formData.displayName}
          onChange={handleChange('displayName')}
          helperText="This will be displayed across the platform"
        />
        <TextField
          fullWidth
          label="Email"
          value={formData.email}
          disabled
          helperText="Email cannot be changed"
        />
        <TextField
          fullWidth
          label="Bio"
          value={formData.bio}
          onChange={handleChange('bio')}
          multiline
          rows={3}
          helperText="Brief description about yourself"
          data-grid-span={12}
        />
        <TextField
          fullWidth
          label="Company"
          value={formData.company}
          onChange={handleChange('company')}
        />
        <TextField
          fullWidth
          label="Location"
          value={formData.location}
          onChange={handleChange('location')}
        />
        <TextField
          fullWidth
          label="Website"
          value={formData.website}
          onChange={handleChange('website')}
          type="url"
          data-grid-span={12}
        />
      </GridLayout>

      {/* Save Button */}
      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <LogoutButton
          variant="outlined"
          color="error"
          onLogoutSuccess={() => navigate('/')}
        />
        <Button
          variant="contained"
          onClick={handleSave}
          loading={saving}
          startIcon={<SaveIcon sx={{ color: 'var(--theme-on-primary)' }} />}
        >
          Save Changes
        </Button>
      </Box>
    </Box>
  );
}
